import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { QRCodeSVG } from "qrcode.react";
import ecoLogo from "../../../assets/images/Logo-2.png";
import lifeLogo from "../../../assets/Life.png";
import dselLogo from "../../../assets/images/schoolEdu-logo.png";
import prakriti from "../../../assets/images/prakarti-img.png";
import cornerLeaves from "../../../assets/images/swachhata/cert-leaves-corner.png";
import shsLogo from "../../../assets/images/swachhata/shs-logo-2026.png";
import { Confetti } from "./SwachhataBits";
import { BINS } from "./swachhataContent";
import { CERT_REGIONAL, CERT_REGIONAL_LANGS } from "./swachhataCertLangs";
import "./Swachhata.css";
import "./SwachhataCertificate.css";

/*
 * Swachhata Hi Seva — participation certificate (schools AND students).
 *
 * <CertificateArt> is the printable A4-landscape sheet: 1200×848 px, absolute
 * layout, and deliberately NO heading tags so the portal's global h1/h2 rules
 * (photoEdit_1280-1389.css forces h1 to 20px on phones) cannot touch it.
 * <SwachhataCertificatePanel> wraps it with the language switch, fit-to-screen
 * scaling and PNG / PDF download (html2canvas + jsPDF — the same pipeline as
 * the plantation certificate in Components/Certificate.jsx).
 *
 * The certificate id (SHS26-SCH-… / SHS26-STU-…) and issue date are issued by
 * the server with the quiz result; the QR points at
 * /swachhata-hi-seva/verify/<id>, a public page backed by
 * POST /swachhata/public/verify-certificate.
 *
 * html2canvas caveats honoured here: no background-clip:text, no CSS filter /
 * clip-path on the sheet, inline-SVG text uses system fonts only (an SVG is
 * rasterised in isolation and cannot see web fonts — the score, labels and
 * banner text are therefore HTML overlays), every image is a bundled asset.
 */

export const CERT_W = 1200;
export const CERT_H = 848; // A4 landscape (297 × 210) at 1200 px wide

const T = {
  en: {
    kicker: "Solid Waste Management Quiz 2026",
    kickerSchool: "Swachhata Hi Seva 2026",
    title: "Certificate",
    script: "of Participation",
    presented: "This certificate is presented to",
    student: "for successfully participating in the Solid Waste Management Quiz 2026 under the aegis of Eco Clubs for Mission LiFE.",
    school: "for successfully participating in Swachhata Hi Seva 2026 – Solid Waste Management Drive under the aegis of Eco Clubs for Mission LiFE.",
    klass: "Class",
    udise: "UDISE",
    date: "Date of issue",
    certId: "Certificate ID",
    scan: "Scan to verify",
    verifyAt: "Digitally issued · verify online at",
    seal: "Swachhata Hi Seva",
    micro: "SWACHHATA HI SEVA 2026 · ECO CLUBS FOR MISSION LiFE · MINISTRY OF EDUCATION · GOVERNMENT OF INDIA · ",
  },
  hi: {
    kicker: "ठोस अपशिष्ट प्रबंधन प्रश्नोत्तरी 2026",
    kickerSchool: "स्वच्छता ही सेवा 2026",
    title: "सहभागिता प्रमाण पत्र",
    script: "",
    presented: "यह प्रमाण पत्र प्रदान किया जाता है",
    student: "मिशन लाइफ के अंतर्गत ईको क्लब्स के तत्वावधान में आयोजित “ठोस अपशिष्ट प्रबंधन नियम, 2026” विषयक प्रश्नोत्तरी में सक्रिय सहभागिता करते हुए इसे सफलतापूर्वक पूर्ण किया।",
    school: "को ईको क्लब्स फॉर मिशन लाइफ के तत्वावधान में स्वच्छता ही सेवा 2026 – ठोस अपशिष्ट प्रबंधन अभियान में सफलतापूर्वक सहभागिता हेतु।",
    klass: "कक्षा",
    udise: "UDISE",
    date: "जारी करने की तिथि",
    certId: "प्रमाण पत्र आईडी",
    scan: "सत्यापन हेतु स्कैन करें",
    verifyAt: "डिजिटल रूप से जारी · ऑनलाइन सत्यापन",
    seal: "स्वच्छता ही सेवा",
    micro: "SWACHHATA HI SEVA 2026 · ECO CLUBS FOR MISSION LiFE · MINISTRY OF EDUCATION · GOVERNMENT OF INDIA · ",
  },
};

export const CERT_LANGS = [
  { key: "bi", label: "Bilingual (English + हिन्दी)" },
  { key: "en", label: "English" },
  { key: "hi", label: "हिन्दी (Hindi)" },
];

// The regional translations are the student quiz wording, so a school
// certificate only offers English / Hindi.
export const certLangsFor = (kind) => (kind === "student" ? [...CERT_LANGS, ...CERT_REGIONAL_LANGS] : CERT_LANGS);

const pad = (n) => String(n).padStart(2, "0");
const fmtShort = (d) => `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;

// Flatten a participation (school) or attempt (student) record into what the
// sheet prints. `kind` is "school" | "student".
export function certificateData(kind, p, { origin } = {}) {
  const base = origin || window.location.origin;
  const quiz = p?.quiz || {};
  const school = p?.school || {};
  const cert = p?.certificate || null;
  const udise = p?.udise_id ? String(p.udise_id) : "";
  const earnedAt = kind === "school" ? p?.survey?.firstSubmittedAt : quiz.submittedAt;
  const issued = cert?.issuedAt ? new Date(cert.issuedAt) : earnedAt ? new Date(earnedAt) : new Date();
  const total = quiz.total || 10;
  const score = quiz.score ?? 0;
  return {
    kind,
    name: kind === "school" ? school.school_name || (udise ? `School ${udise}` : "") : String(p?.student?.name || "").toUpperCase(),
    klass: p?.student?.class || "",
    age: p?.student?.age ?? null,
    schoolName: school.school_name || "",
    state: school.state || "",
    place: [kind === "school" ? school.block : null, school.district, school.state].filter(Boolean).join(", "),
    udise,
    score,
    total,
    percent: quiz.percent ?? Math.round((score / total) * 100),
    durationSec: quiz.durationSec,
    submittedAt: quiz.submittedAt ? new Date(quiz.submittedAt) : null,
    id: cert?.id || "",
    issued,
    verifyUrl: cert?.id ? `${base}/swachhata-hi-seva/verify/${cert.id}` : "",
    verifyHost: base.replace(/^https?:\/\//, ""),
  };
}

// ─── SVG ornaments ─────────────────────────────────────────────────────────
// Every ornament is an <svg> inside a positioned <div>: html2canvas rasterises
// each svg in isolation (patterns, gradients and textPath all survive) but
// silently drops an <svg> that is itself position:absolute.

const GOLD = "#c9a24a";
const GOLD_DARK = "#a67c2e";
const GREEN = "#1b5e33";
const GREEN_2 = "#2e7d46";
const SAFFRON = "#f28c28";

// Eight-petal lotus medallion (corner ornaments + divider centre).
function Lotus({ cx, cy, r = 22 }) {
  const petals = Array.from({ length: 8 }, (_, i) => i * 45);
  return (
    <g transform={`translate(${cx} ${cy})`}>
      <circle r={r} fill="#fffaf0" stroke={GOLD} strokeWidth="1.6" />
      <circle r={r - 4} fill="none" stroke={GOLD} strokeWidth="0.6" strokeDasharray="2 2" />
      {petals.map((a) => (
        <ellipse key={a} rx={r * 0.2} ry={r * 0.56} cy={-r * 0.42} transform={`rotate(${a})`} fill={GREEN_2} stroke={GOLD} strokeWidth="0.8" />
      ))}
      <circle r={r * 0.2} fill={SAFFRON} stroke={GOLD_DARK} strokeWidth="0.8" />
    </g>
  );
}

// The full-sheet frame: guilloche band, gold rules, lotus corners, mid-edge
// diamonds, faint leaf paper texture and the security micro-text ring.
function Frame({ micro }) {
  const uid = useId().replace(/[^a-zA-Z0-9]/g, "");
  const gp = `shscGuil${uid}`;
  const lp = `shscLeaf${uid}`;
  const paths = {
    top: `shscMT${uid}`, bottom: `shscMB${uid}`, left: `shscML${uid}`, right: `shscMR${uid}`,
  };
  const line = micro.repeat(6);
  const Micro = ({ id }) => (
    <text fontSize="6.4" letterSpacing="1.3" fontWeight="700" fill={GREEN} opacity="0.55" fontFamily="Arial, Helvetica, sans-serif">
      <textPath href={`#${id}`}>{line}</textPath>
    </text>
  );
  return (
    <div className="shsc-frame" aria-hidden="true">
      <svg viewBox="0 0 1200 848">
        <defs>
          <pattern id={gp} width="28" height="26" patternUnits="userSpaceOnUse">
            <path d="M0 13 Q7 0 14 13 T28 13" fill="none" stroke={GOLD} strokeWidth="0.9" />
            <path d="M0 13 Q7 26 14 13 T28 13" fill="none" stroke={GOLD} strokeWidth="0.9" />
            <path d="M0 4 Q7 13 14 4 T28 4" fill="none" stroke={GREEN_2} strokeWidth="0.5" opacity="0.5" />
            <path d="M0 22 Q7 13 14 22 T28 22" fill="none" stroke={GREEN_2} strokeWidth="0.5" opacity="0.5" />
            <circle cx="14" cy="13" r="1.1" fill={GOLD_DARK} />
          </pattern>
          <pattern id={lp} width="46" height="46" patternUnits="userSpaceOnUse" patternTransform="rotate(25)">
            <path d="M6 20 C6 11 12 5 21 5 C21 14 15 20 6 20 Z" fill={GREEN_2} opacity="0.06" />
            <path d="M28 40 C28 33 33 28 40 28 C40 35 35 40 28 40 Z" fill={SAFFRON} opacity="0.05" />
          </pattern>
          <path id={paths.top} d="M70 53 H1130" />
          <path id={paths.bottom} d="M70 799 H1130" />
          <path id={paths.left} d="M55 784 V64" />
          <path id={paths.right} d="M1145 64 V784" />
        </defs>
        {/* paper texture */}
        <rect x="44" y="44" width="1112" height="760" fill={`url(#${lp})`} />
        {/* guilloche band between the two frames */}
        <path d="M14 14 H1186 V834 H14 Z M40 40 V808 H1160 V40 Z" fill="#f7efd8" fillRule="evenodd" />
        <path d="M14 14 H1186 V834 H14 Z M40 40 V808 H1160 V40 Z" fill={`url(#${gp})`} fillRule="evenodd" />
        <rect x="14" y="14" width="1172" height="820" fill="none" stroke={GREEN} strokeWidth="3" />
        <rect x="19" y="19" width="1162" height="810" fill="none" stroke={GOLD} strokeWidth="0.8" />
        <rect x="40" y="40" width="1120" height="768" fill="none" stroke={GOLD_DARK} strokeWidth="1.6" />
        <rect x="46" y="46" width="1108" height="756" fill="none" stroke={GREEN} strokeWidth="0.7" opacity="0.5" />
        {/* mid-edge diamonds */}
        {[[600, 27], [600, 821], [27, 424], [1173, 424]].map(([x, y]) => (
          <g key={`${x}-${y}`} transform={`translate(${x} ${y}) rotate(45)`}>
            <rect x="-7" y="-7" width="14" height="14" fill="#fffaf0" stroke={GOLD_DARK} strokeWidth="1.2" />
            <rect x="-3" y="-3" width="6" height="6" fill={GREEN_2} />
          </g>
        ))}
        {/* security micro-text (English — left off the regional-language sheets) */}
        {micro && (
          <>
            <Micro id={paths.top} />
            <Micro id={paths.bottom} />
            <Micro id={paths.left} />
            <Micro id={paths.right} />
          </>
        )}
      </svg>
    </div>
  );
}

// Divider under the title: tapered gold lines meeting a small lotus.
function Divider() {
  return (
    <div className="shsc-divider" aria-hidden="true">
      <svg viewBox="0 0 460 40">
        <path d="M10 20 H200" stroke={GOLD} strokeWidth="1.4" strokeLinecap="round" />
        <path d="M260 20 H450" stroke={GOLD} strokeWidth="1.4" strokeLinecap="round" />
        <path d="M40 20 H200" stroke={GREEN_2} strokeWidth="0.6" transform="translate(0 4)" opacity="0.6" />
        <path d="M260 20 H420" stroke={GREEN_2} strokeWidth="0.6" transform="translate(0 4)" opacity="0.6" />
        <path d="M188 20 l-8 -6 M188 20 l-8 6" stroke={GOLD_DARK} strokeWidth="1.2" fill="none" />
        <path d="M272 20 l8 -6 M272 20 l8 6" stroke={GOLD_DARK} strokeWidth="1.2" fill="none" />
        <Lotus cx={230} cy={20} r={15} />
      </svg>
    </div>
  );
}

// Three chasing arrows (the recycling mark) — watermark + seal crest.
function Recycle({ className, stroke = "#1b7f3b", width = 2.6 }) {
  return (
    <svg className={className} viewBox="-16 -16 32 32" aria-hidden="true">
      <g fill="none" stroke={stroke} strokeWidth={width} strokeLinecap="round" strokeLinejoin="round">
        <path d="M-9.5 3.5 A11 11 0 0 1 -3 -9" />
        <path d="M3 -9 A11 11 0 0 1 9.5 3.5" />
        <path d="M9.5 3.5 A11 11 0 0 1 -9.5 3.5" strokeDasharray="14 40" />
        <path d="M-3 -9 l-4.5 -1.5 M-3 -9 l1.5 4.5" />
        <path d="M9.5 3.5 l1.5 -4.5 M9.5 3.5 l-4.5 1.5" />
        <path d="M-9.5 3.5 l4.5 1.5 M-9.5 3.5 l-1.5 -4.5" />
      </g>
    </svg>
  );
}

// The four segregation bins (colours from BINS in swachhataContent.js). Labels
// are HTML under each svg so they can use the sheet's web fonts.
const BIN_HI = {
  wet: ["गीला कचरा", "हरा डिब्बा"],
  dry: ["सूखा कचरा", "नीला डिब्बा"],
  sanitary: ["सैनिटरी कचरा", "लाल डिब्बा"],
  special: ["विशेष देखभाल कचरा", "काला डिब्बा"],
};

function CertBin({ colour, dark }) {
  const gid = `shscBin${useId().replace(/[^a-zA-Z0-9]/g, "")}`;
  return (
    <svg viewBox="0 0 60 72" aria-hidden="true">
      <defs>
        <linearGradient id={gid} x1="0" x2="1">
          <stop offset="0" stopColor={colour} />
          <stop offset="1" stopColor={dark} />
        </linearGradient>
      </defs>
      <path d="M23 9 V5 a2 2 0 0 1 2 -2 h10 a2 2 0 0 1 2 2 V9" fill="none" stroke={dark} strokeWidth="2.4" />
      <rect x="4" y="9" width="52" height="8" rx="3" fill={dark} />
      <path d="M8 19 h44 l-3.6 46 a5 5 0 0 1 -5 4.6 H16.6 a5 5 0 0 1 -5 -4.6 z" fill={`url(#${gid})`} />
      <path d="M17 25 v38 M43 25 v38" stroke="rgba(255,255,255,0.22)" strokeWidth="2.4" strokeLinecap="round" />
      <g transform="translate(30 44) scale(0.78)" fill="none" stroke="#fff" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M-9.5 3.5 A11 11 0 0 1 -3 -9" />
        <path d="M3 -9 A11 11 0 0 1 9.5 3.5" />
        <path d="M9.5 3.5 A11 11 0 0 1 -9.5 3.5" strokeDasharray="14 40" />
        <path d="M-3 -9 l-4.5 -1.5 M-3 -9 l1.5 4.5" />
        <path d="M9.5 3.5 l1.5 -4.5 M9.5 3.5 l-4.5 1.5" />
        <path d="M-9.5 3.5 l4.5 1.5 M-9.5 3.5 l-1.5 -4.5" />
      </g>
    </svg>
  );
}

function CertBins({ lang, reg }) {
  return (
    <div className="shsc-bins">
      {BINS.map((b, i) => {
        const [hiName, hiColour] = BIN_HI[b.key] || [];
        // regional sheets print only the supplied bin name (no colour line — none was translated)
        const first = reg ? reg.bins[i] : lang === "hi" ? hiName : b.name;
        const second = reg ? "" : lang === "bi" ? hiName : lang === "hi" ? hiColour : b.colour;
        return (
          <div key={b.key} className="shsc-bin">
            <div className="shsc-bin__art"><CertBin colour={b.hex} dark={b.dark} /></div>
            <b dir={reg?.dir} style={reg ? { color: b.dark } : undefined}>{first}</b>
            {second && <em style={{ color: b.dark }}>{second}</em>}
          </div>
        );
      })}
    </div>
  );
}

// ─── The sheet ─────────────────────────────────────────────────────────────

export function CertificateArt({ data, lang = "bi", certRef }) {
  // A regional language is a STANDALONE sheet: only its supplied lines are printed, no English.
  const reg = data.kind === "student" ? CERT_REGIONAL[lang] || null : null;
  const isHi = lang === "hi";
  const t = isHi ? T.hi : T.en;
  const bi = lang === "bi";
  const body = data.kind === "school" ? t.school : t.student;
  // Same layout as English: the supplied sentence is split at the name — the words before it
  // take the "presented to" line, the words after it the statement line.
  const [regBefore = "", regAfter = ""] = reg ? reg.text.split("{name}") : [];
  const presented = reg ? regBefore.trim() : t.presented;
  const statement = reg ? regAfter.trim().replace(/^-/, "") : body;
  // no "Class" / "UDISE" labels were translated, so the regional meta carries values only
  // A student certificate prints the school only when the UDISE code actually
  // resolved to one. It used to fall back to the raw code, so a mistyped one
  // ("11CHARACTER") was printed on the certificate as if it were a school.
  const shownMeta = reg ? [data.schoolName, data.state].filter(Boolean).join(" · ") : null;
  const rootRef = useRef(null);
  const setRoot = useCallback((el) => {
    rootRef.current = el;
    if (typeof certRef === "function") certRef(el);
    else if (certRef) certRef.current = el;
  }, [certRef]);

  // The statement column sits above the footer row; a long regional sentence
  // (or a long name) would run into the mascot / bins / QR, so step the two
  // statement lines down until the column clears the footer. Layout offsets
  // are used (not getBoundingClientRect) because the panel scales the sheet.
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    let alive = true;
    const fit = () => {
      if (!alive) return;
      const column = root.querySelector(".shsc-cert__body");
      const foot = root.querySelector(".shsc-cert__foot");
      const lines = [...root.querySelectorAll(".shsc-cert__text, .shsc-cert__text-hi, .shsc-cert__presented--reg")];
      if (!column || !foot || !lines.length) return;
      lines.forEach((n) => { n.style.fontSize = ""; });
      const base = lines.map((n) => parseFloat(window.getComputedStyle(n).fontSize) || 16);
      const limit = foot.offsetTop - 4;
      for (let k = 1; column.offsetTop + column.offsetHeight > limit && k > 0.7; ) {
        k -= 0.03;
        lines.forEach((n, i) => { n.style.fontSize = `${(base[i] * k).toFixed(2)}px`; });
      }
      // a long regional title stays on one line
      const title = root.querySelector(".shsc-cert__title-reg");
      if (title) {
        title.style.fontSize = "";
        let size = parseFloat(window.getComputedStyle(title).fontSize) || 48;
        while (title.scrollWidth > title.clientWidth + 1 && size > 26) {
          size -= 2;
          title.style.fontSize = `${size}px`;
        }
      }
    };
    fit();
    const fonts = document.fonts;
    if (fonts) {
      fonts.ready.then(fit).catch(() => {});
      fonts.addEventListener?.("loadingdone", fit);
    }
    return () => {
      alive = false;
      fonts?.removeEventListener?.("loadingdone", fit);
    };
  }, [lang, data.kind, data.name, data.klass, data.schoolName, data.state, data.udise, data.place]);

  // Single strings (not adjacent JSX text nodes): the portal's translation
  // extension re-flows adjacent text nodes and eats the spaces between them.
  const meta =
    data.kind === "school"
      ? [`${t.udise} ${data.udise}`, data.place].filter(Boolean).join(" · ")
      : [
          data.klass ? `${t.klass} ${data.klass}` : "",
          data.schoolName,
          data.state,
        ]
          .filter(Boolean)
          .join(" · ");

  return (
    <div
      ref={setRoot}
      id="shsc-cert-root"
      className={`shsc-cert shsc-cert--${data.kind} shsc-cert--${reg ? "reg" : lang}${reg ? ` shsc-lang--${lang}` : ""}${reg?.dir === "rtl" ? " shsc-cert--rtl" : ""}`}
      style={reg ? { "--reg-font": reg.font } : undefined}
    >
      <div className="shsc-cert__wash" />
      <Frame micro={reg ? "" : t.micro} />
      <img src={cornerLeaves} alt="" className="shsc-leaves shsc-leaves--tl" />
      <img src={cornerLeaves} alt="" className="shsc-leaves shsc-leaves--tr" />
      <img src={cornerLeaves} alt="" className="shsc-leaves shsc-leaves--bl" />
      <img src={cornerLeaves} alt="" className="shsc-leaves shsc-leaves--br" />
      <div className="shsc-cert__watermark" aria-hidden="true"><Recycle stroke="#1b7f3b" width={2.2} /></div>

      <div className="shsc-cert__head">
        <div className="shsc-cert__head-left">
          <img src={lifeLogo} alt="Mission LiFE" className="shsc-cert__logo-life" />
          <img src={ecoLogo} alt="Eco Clubs for Mission LiFE" className="shsc-cert__logo-eco" />
        </div>
        <img src={shsLogo} alt="Swachhata Hi Seva 2026" className="shsc-cert__logo-shs" />
        <div className="shsc-cert__head-right">
          <img src={dselLogo} alt="Department of School Education & Literacy" className="shsc-cert__logo-dsel" />
        </div>
      </div>
      <div className="shsc-tricolour" aria-hidden="true"><i /><i /><i /></div>

      <div className="shsc-cert__kicker-wrap">
        <span className="shsc-cert__kicker" dir={reg?.dir}>
          <span className="shsc-cert__kicker-dot" />
          {reg ? reg.kicker : data.kind === "school" ? t.kickerSchool : t.kicker}
          <span className="shsc-cert__kicker-dot" />
        </span>
      </div>

      <div className="shsc-cert__title">
        {reg ? (
          <>
            <div className="shsc-cert__title-main shsc-cert__title-reg" dir={reg.dir}>{reg.titleMain}</div>
            {reg.titleSub && <div className="shsc-cert__title-script shsc-cert__title-sub-reg" dir={reg.dir}>{reg.titleSub}</div>}
          </>
        ) : isHi ? (
          <div className="shsc-cert__title-hi-main">{T.hi.title}</div>
        ) : (
          <>
            <div className="shsc-cert__title-main">{T.en.title}</div>
            <div className="shsc-cert__title-script">{T.en.script}</div>
            {bi && <div className="shsc-cert__title-hi">{T.hi.title}</div>}
          </>
        )}
      </div>
      <Divider />

      <div className="shsc-cert__body">
        <div className={`shsc-cert__presented${reg ? " shsc-cert__presented--reg" : ""}`} dir={reg?.dir}>{presented}</div>
        <div className="shsc-cert__name">{data.name}</div>
        <div className="shsc-cert__rule" />
        {(reg ? shownMeta : meta) && <div className="shsc-cert__meta">{reg ? shownMeta : meta}</div>}
        <div className={`shsc-cert__text${isHi ? " shsc-cert__text--hi" : ""}${reg ? " shsc-cert__text--reg" : ""}`} dir={reg?.dir}>{statement}</div>
        {/* the name is already printed above — the Hindi line starts without it (and without "को") */}
        {bi && <div className="shsc-cert__text-hi">{T.hi[data.kind].replace(/^को\s+/, "")}</div>}
      </div>

      {/* one row, left → right: mascot | date + id | bins | QR */}
      <div className="shsc-cert__foot">
        <img src={prakriti} alt="" className="shsc-mascot" />
        <div className="shsc-foot__issue">
          {/* "Date of issue" / "Certificate ID" were not translated: regional sheets print the values only */}
          <div>
            {!reg && <span>{t.date}</span>}
            <b>{fmtShort(data.issued)}</b>
          </div>
          <div>
            {!reg && <span>{t.certId}</span>}
            <b className="shsc-foot__id">{data.id || "—"}</b>
          </div>
        </div>
        <CertBins lang={lang} reg={reg} />
        {data.verifyUrl && (
          <div className="shsc-qr">
            <div className="shsc-qr__box">
              <QRCodeSVG value={data.verifyUrl} size={64} level="M" bgColor="#ffffff" fgColor="#14301f" />
            </div>
            <div className="shsc-qr__cap">
              {!reg && <b>{t.scan}</b>}
              <em>{`${data.verifyHost}/swachhata-hi-seva/verify`}</em>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Panel: language + fit + download ──────────────────────────────────────

const waitForImages = (root) =>
  Promise.all(
    Array.from(root.querySelectorAll("img")).map((img) =>
      img.complete
        ? Promise.resolve()
        : new Promise((resolve) => {
            img.onload = resolve;
            img.onerror = resolve;
          })
    )
  );

export default function SwachhataCertificatePanel({
  kind = "student",
  participation,
  onDownloaded,
  onBack,
  backLabel = "← Back",
  onExit,
  exitLabel = "✕ Exit",
  celebrate = false,
  compact = false,
  verifyOrigin,
  autoDownload = false, // "pdf" | "png" — download once as soon as the sheet is ready
}) {
  const data = useMemo(() => certificateData(kind, participation, { origin: verifyOrigin }), [kind, participation, verifyOrigin]);
  const langs = certLangsFor(kind);
  const [pickedLang, setLang] = useState("bi");
  const lang = langs.some((l) => l.key === pickedLang) ? pickedLang : "bi";
  const [fit, setFit] = useState(true);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const [scale, setScale] = useState(1);
  const certRef = useRef(null);
  const stageRef = useRef(null);
  const scalerRef = useRef(null);

  // Fit the 1200 px sheet to the container (transform scale), never upscale.
  useLayoutEffect(() => {
    const el = stageRef.current;
    if (!el) return undefined;
    const measure = () => setScale(Math.min(1, (el.clientWidth - 2) / CERT_W));
    measure();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const effScale = fit ? scale : 1;

  // Rasterise the sheet at 2× (2400 × 1696). The scale transform is lifted for
  // the capture so html2canvas sees the true 1200 × 848 box.
  const capture = useCallback(async () => {
    const el = certRef.current;
    const scaler = scalerRef.current;
    const stage = stageRef.current;
    if (!el) throw new Error("Certificate not ready.");
    if (document.fonts && document.fonts.ready) {
      try { await document.fonts.ready; } catch { /* ignore */ }
    }
    await waitForImages(el);
    const prev = { transform: scaler.style.transform, height: stage.style.height, overflow: stage.style.overflow };
    scaler.style.transform = "none";
    stage.style.height = `${CERT_H}px`;
    stage.style.overflow = "visible";
    try {
      return await html2canvas(el, {
        scale: 2,
        useCORS: true,
        allowTaint: false,
        backgroundColor: "#fffdf6",
        width: CERT_W,
        height: CERT_H,
        windowWidth: CERT_W,
        windowHeight: CERT_H,
        scrollX: 0,
        scrollY: 0,
        logging: false,
        // The clone re-runs every CSS animation from frame 0 (the panel's
        // entrance scale, the shell's pop, the shine sweep…), so html2canvas
        // measured the sheet mid-animation and produced sub-pixel gradient
        // tiles that throw. Freeze everything in the cloned document.
        onclone: (doc) => {
          const style = doc.createElement("style");
          style.textContent = "*, *::before, *::after { animation: none !important; transition: none !important; }";
          doc.head.appendChild(style);
        },
      });
    } finally {
      scaler.style.transform = prev.transform;
      stage.style.height = prev.height;
      stage.style.overflow = prev.overflow;
    }
  }, []);

  const fileBase = `SwachhataHiSeva_Certificate_${data.id || data.kind}`;

  const downloadPng = async () => {
    setErr("");
    setBusy("png");
    try {
      const canvas = await capture();
      const a = document.createElement("a");
      a.href = canvas.toDataURL("image/png");
      a.download = `${fileBase}.png`;
      a.click();
      onDownloaded && onDownloaded("png");
    } catch (e) {
      console.error("Swachhata certificate PNG export failed", e);
      setErr("Could not generate the image. Please try again.");
    } finally {
      setBusy("");
    }
  };

  const downloadPdf = async () => {
    setErr("");
    setBusy("pdf");
    try {
      const canvas = await capture();
      const pdf = new jsPDF("landscape", "pt", "a4");
      const pw = pdf.internal.pageSize.getWidth();
      const ph = pdf.internal.pageSize.getHeight();
      const r = Math.min(pw / canvas.width, ph / canvas.height);
      const w = canvas.width * r;
      const h = canvas.height * r;
      pdf.addImage(canvas.toDataURL("image/png"), "PNG", (pw - w) / 2, (ph - h) / 2, w, h);
      pdf.save(`${fileBase}.pdf`);
      onDownloaded && onDownloaded("pdf");
    } catch (e) {
      console.error("Swachhata certificate PDF export failed", e);
      setErr("Could not generate the PDF. Please try again.");
    } finally {
      setBusy("");
    }
  };

  // Straight after the quiz the certificate opens AND downloads. The flag is
  // set inside the timer so React StrictMode's mount/unmount/mount still
  // downloads exactly once.
  const autoDone = useRef(false);
  const downloadRef = useRef(null);
  downloadRef.current = autoDownload === "png" ? downloadPng : downloadPdf;
  useEffect(() => {
    if (!autoDownload || autoDone.current || !data.id) return undefined;
    const t = setTimeout(() => {
      if (autoDone.current) return;
      autoDone.current = true;
      downloadRef.current();
    }, 900);
    return () => clearTimeout(t);
  }, [autoDownload, data.id]);

  return (
    <div className={`shsc-panel ${compact ? "shsc-panel--compact" : ""}`}>
      <Confetti run={celebrate} />
      <div className="shsc-panel__head">
        <div>
          <span className="shs-badge-pill">
            <i />
            {kind === "school" ? "School participation certificate" : "Student participation certificate"}
          </span>
          <div className="shsc-panel__title">{data.name || "Your certificate"}</div>
          <div className="shsc-panel__sub">
            {data.id
              ? `Certificate ID ${data.id} · issued ${fmtShort(data.issued)} · the QR on the sheet verifies it online.`
              : "Your certificate ID is being issued — reload if it does not appear in a moment."}
          </div>
        </div>
        <div className="shsc-panel__controls">
          <label className="shsc-select">
            <span>Language</span>
            <select value={lang} onChange={(e) => setLang(e.target.value)}>
              {langs.map((l) => (
                <option key={l.key} value={l.key}>{l.label}</option>
              ))}
            </select>
          </label>
          <button type="button" className="shs-btn shs-btn--ghost shs-btn--sm" onClick={() => setFit((f) => !f)}>
            {fit ? "Actual size" : "Fit to screen"}
          </button>
        </div>
      </div>

      <div ref={stageRef} className={`shsc-stage ${fit ? "" : "shsc-stage--scroll"}`} style={{ height: CERT_H * effScale + 2 }}>
        <div ref={scalerRef} className="shsc-scaler" style={{ transform: `scale(${effScale})` }}>
          <CertificateArt data={data} lang={lang} certRef={certRef} />
        </div>
        <div className="shsc-stage__shine" aria-hidden="true" />
      </div>

      {err && <div className="shs-error" style={{ marginTop: 14 }}>{err}</div>}

      <div className="shsc-panel__actions">
        <button type="button" className="shs-btn shs-btn--orange shs-btn--lg" onClick={downloadPng} disabled={!!busy}>
          {busy === "png" ? "Preparing image…" : "⬇ Download as image (PNG)"}
        </button>
        <button type="button" className="shs-btn shs-btn--primary shs-btn--lg" onClick={downloadPdf} disabled={!!busy}>
          {busy === "pdf" ? "Preparing PDF…" : "⬇ Download as PDF"}
        </button>
        {data.verifyUrl && (
          <a href={data.verifyUrl} target="_blank" rel="noopener noreferrer" className="shs-btn shs-btn--ghost">
            ✓ Verify this certificate
          </a>
        )}
        {onBack && (
          <button type="button" className="shs-btn shs-btn--ghost" onClick={onBack}>{backLabel}</button>
        )}
        {onExit && (
          <button type="button" className="shs-btn shs-btn--exit" onClick={onExit}>{exitLabel}</button>
        )}
      </div>
      {data.verifyUrl && (
        <div className="shsc-panel__note" style={{ wordBreak: "break-all" }}>
          {"Verification link: "}
          <a href={data.verifyUrl} target="_blank" rel="noopener noreferrer" style={{ color: "inherit", fontWeight: 700, textDecoration: "underline" }}>{data.verifyUrl}</a>
        </div>
      )}
      <div className="shsc-panel__note">
        {"Tip: print the PDF on A4 landscape. Anyone can check the certificate by scanning its QR code or entering the ID on the verification page."}
      </div>
    </div>
  );
}
